import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { eq } from "drizzle-orm";
import { z } from "zod";
import { ContainerSmall } from "@/components/primitives/container-small";
import { PageContainer } from "@/components/primitives/layout";
import { DiscoverRecipesSearchResults } from "@/components/views/discover/discover-recipes-search-results";
import { DISCOVER_RECIPES_LIMIT } from "@/components/views/discover/utils";
import { db, ingredients, user } from "@/db/index.server";
import { auth } from "@/lib/auth";
import { optionalStringSearchParam } from "@/lib/searchParams";

const optionalNonNegativeNumber = z.preprocess((value) => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0
    ? numericValue
    : undefined;
}, z.number().optional());

const searchSchema = z.object({
  q: optionalStringSearchParam,
  user: optionalStringSearchParam,
  ingredient: optionalStringSearchParam,
  sort: z.enum(["newest", "oldest"]).optional().catch(undefined),
  type: z
    .enum([
      "all",
      "other",
      "ice_cream",
      "gelato",
      "sherbet",
      "sorbet",
      "frozen_yogurt",
      "ganache",
      "composite",
    ])
    .optional()
    .catch(undefined),
  quality: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => (v === true || v === "true" ? true : undefined))
    .catch(undefined),
  caloriesMin: optionalNonNegativeNumber,
  caloriesMax: optionalNonNegativeNumber,
});

type DiscoverSearch = z.infer<typeof searchSchema>;

const getDiscoverSearchData = createServerFn({ method: "GET" })
  .inputValidator((data: DiscoverSearch) => data)
  .handler(async ({ data }) => {
    const request = getRequest();
    const [session, filterUsers, filterIngredient, { getPublicRecipes }] =
      await Promise.all([
        auth.api.getSession({ headers: request.headers }),
        data.user
          ? db
              .select({ name: user.name })
              .from(user)
              .where(eq(user.id, data.user))
              .limit(1)
          : Promise.resolve([]),
        data.ingredient
          ? db._query.ingredients.findFirst({
              where: eq(ingredients.slug, data.ingredient),
              columns: { name: true },
            })
          : Promise.resolve(undefined),
        import("@/lib/api/getPublicRecipes.server"),
      ]);
    const userId = session?.user?.id ?? null;
    const recipesResponse = await getPublicRecipes({
      query: data.q,
      sort: data.sort,
      type: data.type,
      highQuality: data.quality,
      caloriesMin: data.caloriesMin,
      caloriesMax: data.caloriesMax,
      createdBy: data.user,
      ingredient: data.ingredient,
      limit: DISCOVER_RECIPES_LIMIT,
      userId,
    });

    return {
      ...recipesResponse,
      userFilterName: filterUsers[0]?.name ?? null,
      ingredientFilterName: filterIngredient?.name ?? null,
    };
  });

export const Route = createFileRoute("/discover/search")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Search Recipes - Scoopulator" },
      {
        name: "description",
        content: "Search public ice cream recipes shared by the community.",
      },
    ],
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => getDiscoverSearchData({ data: deps }),
  component: DiscoverSearchPage,
});

function DiscoverSearchPage() {
  const recipesResponse = Route.useLoaderData();

  return (
    <PageContainer>
      <ContainerSmall>
        <DiscoverRecipesSearchResults
          recipes={recipesResponse.recipes}
          nextCursor={recipesResponse.nextCursor}
          hasMore={recipesResponse.hasMore}
          totalCount={recipesResponse.totalCount}
          userFilterName={recipesResponse.userFilterName}
          ingredientFilterName={recipesResponse.ingredientFilterName}
        />
      </ContainerSmall>
    </PageContainer>
  );
}
